import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { oneLight, SyntaxHighlighter } from '../lib/syntax';

interface StreamingTextProps {
  content: string;
}

export function StreamingText({ content }: StreamingTextProps): React.ReactElement {
  return (
    <div>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          code({ className, children, ...props }) {
            const isInline = !className;
            if (isInline) {
              return (
                <code className="rounded bg-muted px-1.5 py-0.5 text-[0.8125rem] text-brand" {...props}>
                  {children}
                </code>
              );
            }
            const language = className?.replace('language-', '') ?? '';
            return (
              <SyntaxHighlighter
                language={language}
                style={oneLight}
                customStyle={{
                  margin: 0,
                  padding: '1rem',
                  background: 'transparent',
                  fontSize: '0.8125rem',
                  lineHeight: '1.625',
                }}
              >
                {String(children as string).replace(/\n$/, '')}
              </SyntaxHighlighter>
            );
          },
          pre({ children }) {
            return <pre className="my-3 overflow-x-auto rounded border border-border bg-muted">{children}</pre>;
          },
          a({ children, href }) {
            return (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand underline decoration-brand/40 hover:decoration-brand"
              >
                {children}
              </a>
            );
          },
          table({ children }) {
            return (
              <div className="my-3 overflow-x-auto">
                <table className="w-full border-collapse border border-border text-sm">{children}</table>
              </div>
            );
          },
          th({ children }) {
            return (
              <th className="border border-border bg-card px-3 py-2 text-left text-muted-foreground">{children}</th>
            );
          },
          td({ children }) {
            return <td className="border border-border px-3 py-2">{children}</td>;
          },
          ul({ children }) {
            return <ul className="my-2 list-disc space-y-1 pl-6">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="my-2 list-decimal space-y-1 pl-6">{children}</ol>;
          },
          blockquote({ children }) {
            return (
              <blockquote className="my-3 border-l-2 border-brand pl-4 text-muted-foreground italic">
                {children}
              </blockquote>
            );
          },
          h1({ children }) {
            return <h1 className="mt-4 mb-2 text-lg font-bold text-foreground">{children}</h1>;
          },
          h2({ children }) {
            return <h2 className="mt-3 mb-2 text-base font-bold text-foreground">{children}</h2>;
          },
          h3({ children }) {
            return <h3 className="mt-3 mb-1 text-sm font-bold text-foreground">{children}</h3>;
          },
          p({ children }) {
            return <p className="my-2 leading-relaxed">{children}</p>;
          },
        }}
      >
        {content}
      </ReactMarkdown>
    </div>
  );
}
